import { useState } from "react";
import { CheckCircle2, ExternalLink, XCircle } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { WhatsAppIcon } from "@/components/site/Icons";
import { api, API, errMsg } from "@/lib/api";
import { waLink } from "@/lib/site";
import { DELIVERED, egp, fmtDate, cardCls, orderLabel } from "@/lib/labels";

const Actions = ({ order, kind, item, onChange, approveText }) => {
  const approve = async () => {
    try { onChange((await api.post(`/customer/orders/${order.id}/${kind}/approve`, { version: item.version })).data); toast.success("اتسجلت موافقتك"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-4 flex flex-wrap items-center gap-3">
      {item.approved_at ? <span className="inline-flex items-center gap-1 rounded-full bg-green-500/20 px-4 py-2 text-sm font-bold text-green-200" data-testid={`${kind}-approved`}><CheckCircle2 className="h-4 w-4" /> وافقت {fmtDate(item.approved_at)}</span>
        : <button onClick={approve} className="font-head rounded-full bg-[var(--km-pink)] px-5 py-2.5 font-bold text-white" data-testid={`${kind}-approve-btn`}>{approveText}</button>}
      <a href={waLink(`أهلاً خرابيش موشن، عندي ملاحظة على ${kind === "script" ? "الاسكربت" : "الفويس أوفر"} (نسخة ${item.version}) في ${orderLabel(order)}`)} target="_blank" rel="noopener noreferrer"
        className="font-head inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 font-bold text-white" data-testid={`${kind}-support-btn`}><WhatsAppIcon className="h-4 w-4" /> كلم الدعم</a>
    </div>
  );
};

const CancelBox = ({ order, onChange }) => {
  const [busy, setBusy] = useState(false);
  if (order.status !== "active" || order.stage >= DELIVERED) return null;
  if (order.stage > 0) return <p className="rounded-2xl bg-black/30 p-4 text-sm text-purple-200/80" data-testid="refund-unavailable">الإلغاء والاسترداد مش متاحين بعد ما بدأنا كتابة الاسكربت.</p>;
  const cancel = async () => {
    setBusy(true);
    try { onChange((await api.post(`/customer/orders/${order.id}/cancel`)).data); toast.success("الأوردر اتلغى والمبلغ رجع لمحفظتك"); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-red-400/30 bg-red-500/5 p-4" data-testid="refund-box">
      <p className="text-sm text-purple-100">تقدر تلغي الأوردر وتسترد {order.is_free ? "إعلانك المجاني" : egp(order.price)} في محفظتك على الموقع بس، طول ما الأوردر في مرحلة «قيد الفهم والمراجعة». بعد ما نبدأ كتابة الاسكربت الاسترداد مش هيبقى متاح.</p>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <button disabled={busy} className="mt-3 inline-flex items-center gap-2 rounded-full bg-red-500 px-5 py-2.5 font-bold text-white disabled:opacity-50" data-testid="customer-cancel-btn"><XCircle className="h-4 w-4" /> الغي واسترد في المحفظة</button>
        </AlertDialogTrigger>
        <AlertDialogContent dir="rtl" className="border-purple-500/30 bg-[var(--km-bg2)] text-white">
          <AlertDialogHeader className="text-right sm:text-right">
            <AlertDialogTitle>متأكد إنك عايز تلغي الأوردر؟</AlertDialogTitle>
            <AlertDialogDescription className="text-purple-200/80">المبلغ هيرجع لمحفظتك على الموقع، مش لفودافون كاش أو إنستاباي.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:justify-start">
            <AlertDialogAction onClick={cancel} className="bg-red-500 hover:bg-red-600" data-testid="customer-cancel-confirm">أيوه، الغي</AlertDialogAction>
            <AlertDialogCancel className="border-purple-500/30 bg-transparent text-white">لا</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export const OrderReviews = ({ order, onChange }) => {
  const delivered = order.stage === DELIVERED && order.status === "active";
  return (
    <div className="mt-6 space-y-6">
      <CancelBox order={order} onChange={onChange} />
      {order.script && (
        <section className={`${cardCls} p-6`} data-testid="script-review">
          <h2 className="font-head mb-1 text-lg font-bold text-white">الاسكربت <span className="text-sm text-purple-300/70">نسخة {order.script.version}</span></h2>
          <p className="mb-3 text-xs text-purple-300/60">{fmtDate(order.script.sent_at)}</p>
          <p className="whitespace-pre-wrap break-words rounded-2xl bg-black/30 p-4 leading-loose text-purple-50" data-testid="script-text">{order.script.text}</p>
          <Actions order={order} kind="script" item={order.script} onChange={onChange} approveText="موافق على الاسكربت" />
        </section>
      )}
      {order.voice && (
        <section className={`${cardCls} p-6`} data-testid="voice-review">
          <h2 className="font-head mb-3 text-lg font-bold text-white">الفويس أوفر <span className="text-sm text-purple-300/70">نسخة {order.voice.version}</span></h2>
          <audio key={order.voice.version} controls controlsList="nodownload" preload="metadata" className="w-full" src={`${API}/customer/orders/${order.id}/voice?v=${order.voice.version}`} data-testid="voice-player" />
          <Actions order={order} kind="voice" item={order.voice} onChange={onChange} approveText="موافق على الفويس أوفر" />
        </section>
      )}
      {delivered && order.delivery_url && (
        <section className={`${cardCls} p-6`} data-testid="final-video">
          <h2 className="font-head mb-3 text-lg font-bold text-white">الفيديو النهائي</h2>
          <a href={order.delivery_url} target="_blank" rel="noopener noreferrer" className="font-head inline-flex items-center gap-2 rounded-full bg-[var(--km-pink)] px-6 py-3 font-bold text-white transition-transform hover:-translate-y-0.5" data-testid="final-video-receive-btn"><ExternalLink className="h-4 w-4" /> استلام الفيديو</a>
        </section>
      )}
    </div>
  );
};
